/**
 * EVO — klaviatura tugmalari yordami (`?`).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ TUGMALAR BOR EDI, LEKIN ULARNI HECH KIM BILMASDI                         ║
 * ║                                                                          ║
 * ║ Dasturda beshta global tugma birikmasi bor va ularning uchtasi HECH      ║
 * ║ QAYERDA yozilmagan edi. Eng yomoni — `Alt+S`: u to'g'ridan-to'g'ri       ║
 * ║ SOTUVNI YAKUNLAYDI, ya'ni pul harakati qiladigan amal, va u haqda        ║
 * ║ yagona eslatma o'zbekcha KOD IZOHIDA turardi.                            ║
 * ║                                                                          ║
 * ║ Klaviatura bu yerda asosiy kirish usuli (GAP_ANALYSIS §C3): sotuvchi     ║
 * ║ kuniga yuzlab qator kiritadi va sichqonchaga har qo'l uzatish vaqt       ║
 * ║ yeydi. Lekin o'rganilmagan tugma — mavjud bo'lmagan tugma.               ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN `?` VA NIMA UCHUN F1 EMAS                                     │
 * │                                                                          │
 * │ `?` — sanoat odati (GitHub, Gmail, Linear, Slack) va u hech qanday       │
 * │ boshqa ma'noga ega emas. `F1` esa brauzerning O'Z yordamini ochadi va    │
 * │ ba'zi tizimlarda uni to'sib bo'lmaydi.                                   │
 * │                                                                          │
 * │ MAYDON ICHIDA ishlamaydi (`isTypingTarget`): mijoz nomida yoki izohda    │
 * │ savol belgisi uchraydi va u matnga tushishi kerak, oyna ochmasligi.      │
 * │ Bu `search-box.tsx` dagi «/» bilan AYNAN bir xil qoida — ikkalasi ham    │
 * │ belgili tugma va ikkalasi ham yozuvga xalaqit bermasligi shart.          │
 * │                                                                          │
 * │ Ctrl+K esa BOSHQACHA: u maydon ichida ham ishlaydi, chunki `Ctrl` bilan  │
 * │ bosilgan harf hech qanday matnga tushmaydi (`command-palette.tsx`).      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ RO'YXAT QO'LDA YOZILGAN — VA BU ONGLI                                    │
 * │                                                                          │
 * │ Tugmalar to'rt xil joyda e'lon qilingan (`command-palette.tsx`,          │
 * │ `search-box.tsx`, `direct-sale.tsx`, `data-table.tsx`) va ularni         │
 * │ avtomatik yig'ish uchun umumiy ro'yxatga ko'chirish kerak bo'lardi.      │
 * │                                                                          │
 * │ Bu RAD ETILDI: o'sha ro'yxat tugmani E'LON qilardi, lekin uni ULAMASDI — │
 * │ ya'ni ikkinchi haqiqat manbai bo'lib qolardi va ajralib ketishi mumkin   │
 * │ bo'lgan joylar SONI kamaymasdi, ortardi. Endi esa ajralish KO'RINADI:    │
 * │ tugma o'zgarsa, uni bu yerda ham yangilash kerakligi shu izohda yozilgan │
 * │ va oyna kuniga ko'p marta ochiladigan joyda turadi.                      │
 * │                                                                          │
 * │ ⚠️ TUGMA QO'SHGANDA / O'ZGARTIRGANDA — SHU RO'YXATNI HAM YANGILANG.      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useT } from '../lib/i18n-context';
import type { TranslationKey } from '../lib/i18n';

/** Bitta birikma: tugmalar + nima qilishi + qayerda ishlashi. */
interface Shortcut {
  readonly keys: readonly string[];
  readonly what: TranslationKey;
  readonly where: TranslationKey;
}

const SHORTCUTS: readonly Shortcut[] = [
  { keys: ['Ctrl', 'K'], what: 'keys.palette', where: 'keys.where.anywhere' },
  { keys: ['?'], what: 'keys.help', where: 'keys.where.outsideField' },
  { keys: ['/'], what: 'keys.focusSearch', where: 'keys.where.listScreens' },
  /*
   * `Alt+S` ro'yxatda QIZIL emas, lekin u BIRINCHI o'rinda turgan pul
   * harakati. Shuning uchun tavsifida «yakunlaydi» so'zi bor: «sotuv»
   * degan qisqa yozuv uni zararsiz ko'rsatardi.
   */
  { keys: ['Alt', 'S'], what: 'keys.completeSale', where: 'keys.where.directSale' },
  { keys: ['Alt', 'N'], what: 'keys.newSale', where: 'keys.where.directSale' },
  { keys: ['Enter'], what: 'keys.openRow', where: 'keys.where.tables' },
  { keys: ['Enter'], what: 'keys.addRow', where: 'keys.where.editTables' },
  { keys: ['Esc'], what: 'keys.close', where: 'keys.where.dialogs' },
  { keys: ['↑', '↓'], what: 'keys.move', where: 'keys.where.lists' },
];

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function ShortcutsHelp(): ReactNode {
  const t = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape' && open) {
        setOpen(false);
        return;
      }

      /*
       * `event.key === '?'` — `Shift+/` ni QO'LDA tekshirmaymiz.
       *
       * Klaviatura joylashuvi har xil: ruscha va o'zbekcha joylashuvda `?`
       * boshqa fizik tugmada turadi. `key` esa brauzer hisoblab bergan
       * BELGI, ya'ni u joylashuvdan qat'iy nazar to'g'ri ishlaydi.
       */
      if (event.key !== '?') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      event.preventDefault();
      setOpen((value) => !value);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('keys.title')}
        className="flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-base font-semibold text-ink">{t('keys.title')}</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('common.close')}
            className="rounded-lg px-2 py-1 text-sm text-ink-muted hover:bg-surface-2 hover:text-ink"
          >
            Esc
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <ul className="flex flex-col gap-2">
            {SHORTCUTS.map((shortcut, index) => (
              <li
                key={`${shortcut.keys.join('+')}-${index}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
              >
                {/*
                  Tugmalar `<kbd>` bilan — bu bezak emas: ekran o'quvchi
                  uni «tugma» deb e'lon qiladi va oddiy matndan ajratadi.
                */}
                <span className="flex shrink-0 items-center gap-1">
                  {shortcut.keys.map((key) => (
                    <kbd
                      key={key}
                      className="evo-num rounded border border-line bg-surface-2 px-1.5 py-0.5 text-xs text-ink"
                    >
                      {key}
                    </kbd>
                  ))}
                </span>
                <span className="min-w-0 flex-1 text-sm text-ink">{t(shortcut.what)}</span>
                <span className="shrink-0 text-xs text-ink-muted">{t(shortcut.where)}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="border-t border-line px-4 py-2 text-xs text-ink-muted">{t('keys.hint')}</p>
      </div>
    </div>
  );
}
